import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  title?: string;
  description?: string;
  tags?: string[];
}

export const VideoModal: React.FC<VideoModalProps> = ({ isOpen, onClose, videoUrl, title, description, tags }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black bg-opacity-75 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl bg-white rounded-xl overflow-hidden shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all"
        >
          <X className="w-6 h-6" />
        </button>
        
        <div className="p-6 pb-4">
          {title && (
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 pr-10">
              {title}
            </h2>
          )}
        </div>
        
        <div className="relative bg-black">
          <div className="relative pt-[56.25%]"> {/* 16:9 Aspect Ratio */}
            <video
              ref={videoRef}
              src={videoUrl}
              className="absolute top-0 left-0 w-full h-full"
              controls
              autoPlay
              playsInline
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

        {description && (
          <div className="p-6 pt-4">
            <p className="text-base text-gray-600 leading-relaxed">
              {description}
            </p>
          </div>
        )}

        {tags && tags.length > 0 && (
          <div className="px-6 pb-6 pt-2">
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};